/**
 * Mock Orders dataset — deterministic, seeded, realistic-looking.
 *
 * Shape matches the union of Alpaca's Orders table (Asset, Side, Order
 * Type, Filled Qty, Avg. Fill Price, Total Amount, Status, Source,
 * Submitted/Filled/Canceled At) and the fields legacy WHB's Orders page
 * expanded per-row (Bot, Strategy, State: CompletedPartially etc.). One
 * shape serves both the Simple (inline-expand list) and
 * Advanced (Alpaca-style data table) views.
 *
 * Timestamp clustering — important for the batch headers:
 *  • ~70% of orders are bot-placed and fire in baskets, so several
 *    orders share the same `submittedAt` minute. The Simple view groups
 *    by `yyyy-MM-dd HH:mm` so these render under one header like
 *    "Apr 17, 2026 at 3:52 PM" with 8 rows beneath.
 *  • ~30% are manual orders spread naturally across trading hours.
 *
 * All timestamps fall inside US equity trading hours (09:30–16:00 ET)
 * on weekdays — gives the demo a realistic feel and prevents "Sunday
 * 2 AM" rows that would look obviously synthetic.
 */

// ---- Types -----------------------------------------------------------------

export type OrderSide = "buy" | "sell";
/**
 * Order types — grouped into two buckets:
 *
 *   Execution (what the broker executes):
 *     - "market" / "limit" / "stop" / "stop_limit"
 *
 *   Platform (WHB-scheduling layer above execution — these orders still
 *   send a market/limit to the broker when they fire, but the order row
 *   records "why" the order exists):
 *     - "pending"   — user-authored order that hasn't been released yet
 *                     (e.g. "submit Monday 10:00 ET"). Sits in the queue
 *                     until the trigger window arrives.
 *     - "scheduled" — recurring, rule-driven orders (e.g. "DCA $500
 *                     into SPY every Friday"). Can live for months.
 *     - "smart"     — conditional / algorithmic (VWAP slicing, bracket,
 *                     trailing stop). WHB decomposes into child orders.
 *
 * Platform-typed orders can be placed without a bot — a user can
 * schedule a manual DCA, or fire a smart bracket from their phone. The
 * view treats them uniformly with bot-placed orders aside from the
 * missing bot chip.
 */
export type OrderType =
  | "market"
  | "limit"
  | "stop"
  | "stop_limit"
  | "pending"
  | "scheduled"
  | "smart"
  /**
   * Synthetic reconciliation entry, not a real broker order. Used to
   * compensate for gaps in WHB's /trading-history feed (e.g. SGOV and
   * other money-market trades WHB filters out). These carry a cash
   * delta that the reconstruction applies like any other order, but
   * they are locally generated and persisted in IndexedDB (survive
   * sign-out). Displayed in the Orders tab with a distinct badge so
   * the user knows they are not original broker rows.
   */
  | "correction";
export type OrderStatus =
  | "filled"
  | "partial"
  | "open"
  | "canceled"
  | "expired"
  | "replaced";
export type OrderSource = "bot" | "manual" | "api";

export interface Order {
  id: string;
  accountId: string;
  /** Set when the order was placed by a bot; `undefined` for manual. */
  botId?: string;
  /** Ticker symbol, e.g. "WM". */
  asset: string;
  /** Company / fund long name, e.g. "Waste Management Inc.". */
  assetName: string;
  side: OrderSide;
  orderType: OrderType;
  /** Filled quantity (may be fractional for dollar-based orders). */
  filledQty: number;
  /** Average fill price per share. */
  avgFillPrice: number;
  /** Dollar total — filledQty × avgFillPrice, rounded. */
  totalAmount: number;
  /** Notional value requested (for dollar-denominated orders). */
  notional?: number;
  /** Limit price for limit orders. */
  limitPrice?: number;
  /** Stop price for stop orders. */
  stopPrice?: number;
  status: OrderStatus;
  source: OrderSource;
  /** Set on rows that came from the "Link Orders" surface
   *  (`/broker-accounts/{id}/orders`) and weren't yet attributed to a
   *  bot's trading history at the broker — manual fills the user
   *  placed in Alpaca after a bot deletion, etc. They appear in the
   *  Orders tab but MUST be excluded from the equity reconstruction:
   *  the matching buy-side fills typically live with the deleted
   *  bot and are not in /trading-history, so feeding the orphan
   *  sells to the reconstruction unbalances the cash math (Sergey
   *  2026-04-30 — single account's Sep 2 equity went to −$10,286
   *  because the SGOV-gap corrector shifted every historical day to
   *  compensate for the unmatched sells). */
  synthetic?: boolean;
  /** ISO 8601 datetime (includes time + TZ offset). */
  submittedAt: string;
  /** Populated when `status` is `filled` or `partial`. */
  filledAt?: string;
  /** Populated when `status` is `canceled`. */
  canceledAt?: string;
  /** Strategy label, e.g. "Martingale". Legacy WHB expand-row field. */
  strategy?: string;
  /** Extended state text, e.g. "CompletedPartially". Legacy WHB field. */
  stateDetail?: string;
  /**
   * When the order is scheduled to release to the broker (ISO 8601).
   * Populated for `pending` and `scheduled` orders; undefined otherwise.
   * For `pending`, this is the one-shot release time; for `scheduled`,
   * it's the *next* upcoming run of the recurring rule.
   */
  scheduledFor?: string;
  /**
   * Human-friendly cadence for `scheduled` orders
   * (e.g. "Every Friday 10:00 ET", "Weekly · Mon").
   */
  scheduleRule?: string;
  /**
   * Label describing the algorithm behind a `smart` order
   * (e.g. "VWAP 30min", "Bracket +2% / −1%", "Trailing 1.5%").
   */
  smartStrategy?: string;
  /**
   * Realized profit in dollars on a closing trade — only set when this
   * order reduced an existing position (Sell on a long, Buy on a short
   * cover). Computed via a FIFO cost-basis pass in the mock generator.
   * Positive = gain, negative = loss.
   */
  realizedProfit?: number;
  /**
   * Realized profit as a percentage of cost basis (same sign as
   * `realizedProfit`). Paired with the $ number so the UI can show
   * both without recomputing.
   */
  realizedProfitPct?: number;
}

// ---- Asset universe --------------------------------------------------------

/**
 * Curated universe of tickers that appear in the demo, with their
 * display names. Drawn from the bot ticker lists in mock-data.ts plus
 * a handful of individual equities so the Simple view has visual
 * variety. Names are truncated to what a trading app typically shows.
 */
const ASSETS: Record<string, string> = {
  // Bot baskets (from BOTS[].tickers)
  SPY: "SPDR S&P 500 ETF Trust",
  QQQ: "Invesco QQQ Trust",
  TLT: "iShares 20+ Year Treasury Bond ETF",
  GLD: "SPDR Gold Shares",
  IEF: "iShares 7-10 Year Treasury Bond ETF",
  TQQQ: "ProShares UltraPro QQQ",
  SQQQ: "ProShares UltraPro Short QQQ",
  UVXY: "ProShares Ultra VIX Short-Term Futures",
  // Individual equities for manual orders
  AAPL: "Apple Inc.",
  MSFT: "Microsoft Corporation",
  NVDA: "NVIDIA Corporation",
  GOOGL: "Alphabet Inc. Class A",
  META: "Meta Platforms Inc.",
  TSLA: "Tesla Inc.",
  AMZN: "Amazon.com Inc.",
  JPM: "JPMorgan Chase & Co.",
  WM: "Waste Management Inc.",
  FAST: "Fastenal Company",
  BLK: "BlackRock Inc.",
  VEEV: "Veeva Systems Inc.",
  INTU: "Intuit Inc.",
  SCHW: "Charles Schwab Corporation"
};

/** Manual-order pool — equities the demo user trades by hand. */
const MANUAL_TICKERS = [
  "AAPL", "MSFT", "NVDA", "GOOGL", "META", "TSLA", "AMZN", "JPM",
  "WM", "FAST", "BLK", "VEEV", "INTU", "SCHW"
];

/**
 * Rough baseline prices (2026-era demo) — orders get a small random
 * jitter on top so fills don't look identical across the year. These
 * are illustrative only; real prices would come from the API.
 */
const BASE_PRICE: Record<string, number> = {
  SPY: 610, QQQ: 545, TLT: 92, GLD: 280, IEF: 97,
  TQQQ: 82, SQQQ: 8, UVXY: 19,
  AAPL: 240, MSFT: 485, NVDA: 180, GOOGL: 215, META: 720,
  TSLA: 390, AMZN: 235, JPM: 295, WM: 223, FAST: 46,
  BLK: 1065, VEEV: 172, INTU: 670, SCHW: 95
};

// ---- Deterministic RNG ----------------------------------------------------

/**
 * Linear-congruential RNG — same family as `walk()` in mock-data.ts so
 * the two sets of demo data feel stylistically consistent. Returns a
 * float in [0, 1). Seed is advanced in place (closure capture).
 */
function makeRng(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };
}

/** Pick a random element from an array. */
function pick<T>(rng: () => number, arr: readonly T[]): T {
  return arr[Math.floor(rng() * arr.length)]!;
}

/** Random integer in [min, max] inclusive. */
function randInt(rng: () => number, min: number, max: number): number {
  return Math.floor(rng() * (max - min + 1)) + min;
}

/** Round to 2 decimal places, matching what the API returns for USD. */
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

// ---- Timestamp helpers -----------------------------------------------------

/** Last weekday on-or-before `d`. */
function clampToWeekday(d: Date): Date {
  const copy = new Date(d);
  const dow = copy.getDay();
  if (dow === 0) copy.setDate(copy.getDate() - 2); // Sunday → Friday
  else if (dow === 6) copy.setDate(copy.getDate() - 1); // Saturday → Friday
  return copy;
}

/**
 * Produce an ISO datetime string for a specific (year, month, day) at
 * `hour:minute` local time, with a trailing "Z" suffix so the string is
 * unambiguous when parsed by Date. We don't try to be timezone-accurate
 * here — the demo renders timestamps via `.toLocaleString` anyway, so
 * what matters is that the string parses and ordering is stable.
 */
function isoAt(d: Date, hour: number, minute: number): string {
  const dt = new Date(d);
  dt.setHours(hour, minute, 0, 0);
  return dt.toISOString();
}

// ---- Order generation ------------------------------------------------------

interface GeneratorConfig {
  accountId: string;
  /** Bot → tickers, for building bot-placed baskets. */
  bots: Array<{ id: string; tickers: string[]; strategy: string }>;
  /** How many orders to generate for this account. */
  count: number;
  seed: number;
}

/**
 * Generate a deterministic order list for a single account spanning
 * the last 12 months up to (but not including) 2026-04-17. Mixes bot
 * batches and manual orders as described in the module header.
 */
function generateOrdersForAccount(cfg: GeneratorConfig): Order[] {
  const rng = makeRng(cfg.seed);
  const orders: Order[] = [];

  // Anchor date — matches `env.Today` ballpark so ordering math is
  // stable across test runs regardless of real clock.
  const endDate = new Date("2026-04-17T20:00:00Z");
  const startDate = new Date(endDate);
  startDate.setFullYear(startDate.getFullYear() - 1);

  let produced = 0;
  let nextId = 1;

  while (produced < cfg.count) {
    // Pick a random weekday in the [startDate, endDate] window.
    const spanDays = Math.floor(
      (endDate.getTime() - startDate.getTime()) / 86_400_000
    );
    const rawDay = new Date(startDate);
    rawDay.setDate(rawDay.getDate() + randInt(rng, 0, spanDays));
    const day = clampToWeekday(rawDay);

    // Decide batch vs manual. Bots fire in clusters (3–6 tickers at
    // once during a rebalance); manual orders are solo.
    const isBatch = cfg.bots.length > 0 && rng() < 0.70;

    if (isBatch) {
      const bot = pick(rng, cfg.bots);
      const basketSize = Math.min(bot.tickers.length, randInt(rng, 3, 6));
      // Basket rebalance happens at one moment — share `submittedAt` to
      // the minute so the Simple view groups these under a single
      // "Apr 17, 2026 at 3:52 PM" header.
      const hour = randInt(rng, 10, 15);       // 10:00–15:59
      const minute = randInt(rng, 0, 59);
      const batchTs = isoAt(day, hour, minute);

      // Pick distinct tickers from the bot's universe.
      const tickers = [...bot.tickers]
        .sort(() => rng() - 0.5)
        .slice(0, basketSize);

      for (const ticker of tickers) {
        if (produced >= cfg.count) break;
        orders.push(
          buildOrder(nextId++, cfg.accountId, bot, ticker, batchTs, rng, "batch")
        );
        produced++;
      }
    } else {
      // Manual single order.
      const ticker = pick(rng, MANUAL_TICKERS);
      const hour = randInt(rng, 10, 15);
      const minute = randInt(rng, 0, 59);
      const ts = isoAt(day, hour, minute);
      orders.push(
        buildOrder(nextId++, cfg.accountId, null, ticker, ts, rng, "manual")
      );
      produced++;
    }
  }

  // Sort newest first — matches how Robinhood/Alpaca all
  // show orders. Consumers that want oldest-first can reverse.
  return orders.sort((a, b) =>
    b.submittedAt.localeCompare(a.submittedAt)
  );
}

/**
 * Build a single Order. Kept separate so batch vs manual code paths
 * share all the "fill in avg price / status / source" logic.
 */
function buildOrder(
  seq: number,
  accountId: string,
  bot: { id: string; strategy: string } | null,
  ticker: string,
  submittedAt: string,
  rng: () => number,
  kind: "batch" | "manual"
): Order {
  const side: OrderSide = rng() < 0.55 ? "buy" : "sell";
  // Manual orders are more likely to use limits; bots typically go
  // market-on-open or market-during-session.
  const orderType: OrderType =
    kind === "batch"
      ? "market"
      : rng() < 0.75
        ? "market"
        : rng() < 0.5
          ? "limit"
          : "stop";

  const basePrice = BASE_PRICE[ticker] ?? 100;
  // ±4% price jitter so fills look varied across the year.
  const avgFillPrice = round2(basePrice * (1 + (rng() - 0.5) * 0.08));

  // Notional ~$1k for bot batches (matches the legacy WHB screenshot's
  // $1,000.01 pattern), ~$250–2000 for manual orders.
  const notional =
    kind === "batch"
      ? round2(1000 + (rng() - 0.5) * 20) // ~$990–1010
      : round2(250 + rng() * 1750);

  const filledQty = round2(notional / avgFillPrice * 1e4) / 1e4; // 4 dp
  const totalAmount = round2(filledQty * avgFillPrice);

  // Status distribution:
  //   90% filled, 4% partial, 3% canceled, 2% expired, 1% open
  // Bot orders are biased toward filled/partial; manual orders see
  // more canceleds (users change their mind, limits expire).
  const statusRoll = rng();
  let status: OrderStatus = "filled";
  if (kind === "manual") {
    if (statusRoll < 0.05) status = "canceled";
    else if (statusRoll < 0.08) status = "expired";
    else if (statusRoll < 0.10) status = "open";
    else if (statusRoll < 0.13) status = "partial";
  } else {
    if (statusRoll < 0.02) status = "canceled";
    else if (statusRoll < 0.06) status = "partial";
  }

  // Derived timestamps.
  const submittedDate = new Date(submittedAt);
  const fillDelay = randInt(rng, 1, 45); // seconds
  const filledAt =
    status === "filled" || status === "partial"
      ? new Date(submittedDate.getTime() + fillDelay * 1000).toISOString()
      : undefined;
  const canceledAt =
    status === "canceled"
      ? new Date(submittedDate.getTime() + randInt(rng, 10, 3600) * 1000).toISOString()
      : undefined;

  // For open orders / canceleds / expireds, fill-derived numbers don't
  // apply — null them out rather than lying.
  const finalFilledQty =
    status === "filled" || status === "partial" ? filledQty : 0;
  const finalAvgFill =
    status === "filled" || status === "partial" ? avgFillPrice : 0;
  const finalTotal =
    status === "filled"
      ? totalAmount
      : status === "partial"
        ? round2(totalAmount * (0.3 + rng() * 0.5)) // 30–80% filled
        : 0;

  return {
    id: `${accountId}-ord-${String(seq).padStart(4, "0")}`,
    accountId,
    botId: bot?.id,
    asset: ticker,
    assetName: ASSETS[ticker] ?? ticker,
    side,
    orderType,
    filledQty: finalFilledQty,
    avgFillPrice: finalAvgFill,
    totalAmount: finalTotal,
    notional,
    // buildOrder only ever emits "market" | "limit" | "stop" (see the
    // rng()-driven assignment above), so the defensive "stop_limit"
    // check that used to live here is dead code — and TS 5.x's
    // narrowing flags it as such. If the generator ever gains a
    // stop_limit branch, add "|| orderType === 'stop_limit'" back to
    // both conditions.
    limitPrice:
      orderType === "limit"
        ? round2(avgFillPrice * (side === "buy" ? 0.995 : 1.005))
        : undefined,
    stopPrice:
      orderType === "stop"
        ? round2(avgFillPrice * (side === "buy" ? 1.01 : 0.99))
        : undefined,
    status,
    source: kind === "batch" ? "bot" : "manual",
    submittedAt,
    filledAt,
    canceledAt,
    strategy: bot?.strategy,
    stateDetail:
      status === "partial"
        ? "CompletedPartially"
        : status === "filled"
          ? "Completed"
          : status === "canceled"
            ? "Canceled"
            : status === "expired"
              ? "Expired"
              : status === "open"
                ? "Accepted"
                : undefined
  };
}

// ---- Platform-type example orders -----------------------------------------

/**
 * Hand-crafted pending/scheduled/smart examples per account. These don't
 * come from the batch generator because:
 *  • They should always appear regardless of seed, so they're fixed.
 *  • Each type needs bespoke fields (release time, cadence, algo label)
 *    that the generator doesn't model.
 *  • They're the visual showcase for the new order types — a handful
 *    per account is plenty; padding them out with random noise would
 *    dilute the illustration.
 *
 * Smart and pending orders can be bot-less (a user can fire a bracket
 * from the phone, or schedule a one-shot release) — the examples lean
 * into that by leaving `botId` unset on most of them.
 */
function buildPlatformExamples(accountId: string): Order[] {
  // Anchor to a stable "now" — same as the generator's endDate so the
  // "in 2 days" / "in 1 week" copy stays coherent with the rest of the
  // dataset.
  const now = new Date("2026-04-17T14:30:00Z"); // mid-session Friday
  const isoOffset = (deltaMin: number, fromIso?: string): string =>
    new Date(
      (fromIso ? new Date(fromIso) : now).getTime() + deltaMin * 60_000
    ).toISOString();

  const ago = (minutes: number): string => isoOffset(-minutes);
  const ahead = (minutes: number): string => isoOffset(minutes);

  return [
    // --- Pending (released at a future time) ---
    {
      id: `${accountId}-ord-p01`,
      accountId,
      asset: "TSLA",
      assetName: ASSETS["TSLA"] ?? "Tesla, Inc.",
      side: "buy",
      orderType: "pending",
      filledQty: 0,
      avgFillPrice: 0,
      totalAmount: 0,
      notional: 500,
      limitPrice: 248.5,
      status: "open",
      source: "manual",
      submittedAt: ago(90),
      scheduledFor: ahead(60 * 3), // 3 hours out
      stateDetail: "Pending release"
    },
    // --- Scheduled (recurring DCA) ---
    {
      id: `${accountId}-ord-s01`,
      accountId,
      asset: "SPY",
      assetName: ASSETS["SPY"] ?? "SPDR S&P 500 ETF Trust",
      side: "buy",
      orderType: "scheduled",
      filledQty: 0,
      avgFillPrice: 0,
      totalAmount: 0,
      notional: 500,
      status: "open",
      source: "manual",
      submittedAt: ago(60 * 24 * 7),      // rule set up a week ago
      scheduledFor: ahead(60 * 24 * 3),   // next run in 3 days
      scheduleRule: "Every Mon 10:00 ET",
      stateDetail: "Recurring schedule"
    },
    {
      id: `${accountId}-ord-s02`,
      accountId,
      asset: "QQQ",
      assetName: ASSETS["QQQ"] ?? "Invesco QQQ Trust",
      side: "buy",
      orderType: "scheduled",
      // Already fired twice — last run produced this fill snapshot.
      filledQty: 2.45,
      avgFillPrice: 510.12,
      totalAmount: round2(2.45 * 510.12),
      notional: 1250,
      status: "filled",
      source: "manual",
      submittedAt: ago(60 * 24 * 14),
      filledAt: ago(60 * 24 * 7),
      scheduledFor: ahead(60 * 24 * 7),
      scheduleRule: "Weekly · Fri close",
      stateDetail: "Last run completed"
    },
    // --- Smart (algo-executed) ---
    {
      id: `${accountId}-ord-sm01`,
      accountId,
      asset: "NVDA",
      assetName: ASSETS["NVDA"] ?? "NVIDIA Corporation",
      side: "sell",
      orderType: "smart",
      // VWAP slicing over 30min — fully filled 20min ago.
      filledQty: 8,
      avgFillPrice: 921.47,
      totalAmount: round2(8 * 921.47),
      notional: 7400,
      status: "filled",
      source: "manual",
      submittedAt: ago(55),
      filledAt: ago(20),
      smartStrategy: "VWAP 30min",
      stateDetail: "Smart routed"
    },
    {
      id: `${accountId}-ord-sm02`,
      accountId,
      asset: "AAPL",
      assetName: ASSETS["AAPL"] ?? "Apple Inc.",
      side: "buy",
      orderType: "smart",
      // Bracket order — parent open, children waiting on triggers.
      filledQty: 0,
      avgFillPrice: 0,
      totalAmount: 0,
      notional: 2000,
      limitPrice: 218.0,
      status: "open",
      source: "manual",
      submittedAt: ago(12),
      smartStrategy: "Bracket +2% / −1%",
      stateDetail: "Parent armed"
    }
  ];
}

// ---- FIFO cost-basis / realized P&L pass ---------------------------------

/**
 * Walk a list of orders in chronological (oldest-first) order, per
 * (account, asset) pair, and annotate closing trades with `realizedProfit`
 * and `realizedProfitPct`.
 *
 * Why FIFO?
 *  • It matches the IRS default for most retail brokerages in the US
 *    (Robinhood / Fidelity / Schwab use FIFO unless the user selects
 *    otherwise). This keeps the demo numbers consistent with what the
 *    user would see in a real statement.
 *  • We don't model tax lots or HIFO/LIFO here — the mock doesn't need
 *    the complexity, and FIFO is a single queue per (account, asset).
 *
 * What counts as a "closing" order?
 *  • The order is filled or partial (no fill → nothing realized).
 *  • The account held a non-zero position of the opposite direction
 *    when the order arrived. Sell-to-close on a long, or Buy-to-cover
 *    on a short, populates realized P&L. Sell-short or Buy-to-open
 *    only grows the queue in the new direction.
 *
 * Position is tracked as a signed quantity: positive = long,
 * negative = short. We flip the queue when position crosses zero on
 * a large closing order (e.g. sell more shares than held → the excess
 * opens a short). Each flip resets the cost basis to the remainder
 * price so the queue stays coherent.
 *
 * We mutate the orders in place — they're freshly constructed for
 * each module load and downstream consumers treat the export as
 * read-only.
 */
function attachRealizedPnL(orders: Order[]): void {
  // Work in chronological order so cost basis accumulates correctly.
  // We sort a shallow copy (not orders) because the caller wants the
  // array order preserved — we only need the traversal order here.
  const chrono = [...orders].sort((a, b) =>
    a.submittedAt.localeCompare(b.submittedAt)
  );

  // key = `${accountId}|${asset}` — lots store signed quantity per entry
  // (positive = long, negative = short). Mixing long/short in one queue
  // is impossible by construction: we only push the direction that
  // matches the current position sign.
  type Lot = { qty: number; price: number };
  const queues = new Map<string, Lot[]>();
  const positions = new Map<string, number>(); // signed

  for (const o of chrono) {
    // Only filled or partial fills move shares. Other statuses (open /
    // canceled / expired) don't change the position or realize anything.
    if (o.status !== "filled" && o.status !== "partial") continue;
    if (o.filledQty <= 0) continue;

    // Platform order types (pending / scheduled / smart) can appear as
    // filled (scheduled DCA that already fired, smart order that
    // completed) — they still move shares, so we include them.

    const key = `${o.accountId}|${o.asset}`;
    const pos = positions.get(key) ?? 0;
    const lots = queues.get(key) ?? [];

    // Determine incoming direction: +qty on buy, -qty on sell.
    const delta = o.side === "buy" ? o.filledQty : -o.filledQty;

    if (pos === 0 || Math.sign(delta) === Math.sign(pos)) {
      // Opening (or growing) a position in the same direction. Push
      // a lot; nothing to realize.
      lots.push({ qty: Math.abs(delta), price: o.avgFillPrice });
      positions.set(key, pos + delta);
      queues.set(key, lots);
      continue;
    }

    // Opposing direction → closing (or flipping). Consume FIFO lots.
    let remaining = Math.abs(delta);
    let realized = 0;
    let costBasis = 0;

    while (remaining > 0 && lots.length > 0) {
      const lot = lots[0]!;
      const take = Math.min(lot.qty, remaining);
      // For a long close (pos > 0, incoming sell):
      //   proceeds = take * sellPrice, cost = take * lotPrice
      //   realized = proceeds - cost
      // For a short cover (pos < 0, incoming buy):
      //   proceeds = take * shortEntryPrice (the lot), cost = take * coverPrice
      //   realized = (lotPrice - coverPrice) * take
      // Both collapse to the same formula when signed with position:
      const perShare =
        pos > 0
          ? o.avgFillPrice - lot.price   // long close
          : lot.price - o.avgFillPrice;  // short cover
      realized += perShare * take;
      costBasis += lot.price * take;
      lot.qty -= take;
      remaining -= take;
      if (lot.qty <= 1e-9) lots.shift();
    }

    // Attach realized numbers to the closing order. Round to cents so
    // the UI doesn't show noise digits.
    if (costBasis > 0) {
      o.realizedProfit = round2(realized);
      o.realizedProfitPct =
        Math.round((realized / costBasis) * 10_000) / 100; // 2dp percent
    }

    // If `remaining > 0` after exhausting lots, this order flipped the
    // position. Push a new lot for the leftover in the NEW direction.
    if (remaining > 0) {
      lots.push({ qty: remaining, price: o.avgFillPrice });
    }

    positions.set(key, pos + delta);
    queues.set(key, lots);
  }
}

// ---- Per-account seed sets ------------------------------------------------

/**
 * Build the mock ORDERS array in three passes:
 *   1. Generate per-account batch + manual orders (12-month history).
 *   2. Append hand-crafted platform examples (pending/scheduled/smart).
 *   3. Run the FIFO cost-basis pass to annotate closing trades with
 *      `realizedProfit` + `realizedProfitPct`.
 * Finally, sort newest-first — consumers expect that ordering and
 * `groupOrdersByMinute` is stable.
 *
 * Wrapped in an IIFE so the intermediate mutable array never escapes
 * this module — consumers only see the sealed export.
 */
const RAW_ORDERS: Order[] = [
  // --- Live accounts first (Live-before-Paper convention mirrors
  //     ACCOUNTS ordering in mock-data.ts).
  //
  // Each account gets both a batch-generated history (bot + manual
  // market/limit/stop fills stretched over the last 12 months) AND a
  // small set of hand-crafted platform examples (pending / scheduled /
  // smart). The platform examples are appended via buildPlatformExamples
  // so every account showcases the new order types; they're newer than
  // the generator output (anchored to 2026-04-17) so they naturally
  // float to the top once the list is re-sorted below. ---
  ...generateOrdersForAccount({
    accountId: "acc-live-1",
    bots: [
      {
        id: "bot-live-momentum",
        tickers: ["SPY", "QQQ", "IEF"],
        strategy: "Momentum rotation"
      }
    ],
    count: 36,
    seed: 401
  }),
  ...buildPlatformExamples("acc-live-1"),
  ...generateOrdersForAccount({
    accountId: "acc-live-2",
    bots: [
      {
        id: "bot-live-swing",
        tickers: ["AAPL", "MSFT", "NVDA", "GOOGL"],
        strategy: "Mean reversion"
      }
    ],
    count: 52,
    seed: 409
  }),
  ...buildPlatformExamples("acc-live-2"),
  // --- Paper accounts ---
  ...generateOrdersForAccount({
    accountId: "acc-1",
    bots: [
      { id: "bot-rsi2-v2", tickers: ["TQQQ"], strategy: "Martingale" },
      { id: "bot-fake-rsi", tickers: ["SPY", "QQQ"], strategy: "RSI-2" },
      { id: "bot-martingale-2", tickers: ["SPY"], strategy: "Martingale" }
    ],
    count: 42,
    seed: 101
  }),
  ...buildPlatformExamples("acc-1"),
  ...generateOrdersForAccount({
    accountId: "acc-2",
    bots: [
      {
        id: "bot-paper-v2",
        tickers: ["SPY", "QQQ", "TLT", "GLD", "IEF"],
        strategy: "Momentum rotation"
      }
    ],
    count: 58,
    seed: 202
  }),
  ...buildPlatformExamples("acc-2"),
  ...generateOrdersForAccount({
    accountId: "acc-3",
    bots: [
      {
        id: "bot-ellen-rsi",
        tickers: ["TQQQ", "SQQQ", "UVXY"],
        strategy: "RSI-2"
      }
    ],
    count: 48,
    seed: 303
  }),
  ...buildPlatformExamples("acc-3")
];

// Annotate closing trades with realized P&L, then freeze the sort
// ordering to newest-first for consumer-friendly output.
attachRealizedPnL(RAW_ORDERS);

export const ORDERS: Order[] = RAW_ORDERS.sort((a, b) =>
  b.submittedAt.localeCompare(a.submittedAt)
);

// ---- Helpers for consumers ------------------------------------------------

/**
 * Group a list of orders by shared `submittedAt` clock minute. Used by
 * the Simple view to render one header per batch
 * ("Apr 17, 2026 at 3:52 PM"). Orders that don't share a minute with
 * any other simply form a one-element group.
 *
 * Groups come back newest-first if the input was newest-first.
 */
export function groupOrdersByMinute(
  orders: Order[]
): Array<{ key: string; submittedAt: string; items: Order[] }> {
  const byMinute = new Map<string, Order[]>();
  for (const o of orders) {
    const key = o.submittedAt.slice(0, 16); // yyyy-mm-ddTHH:MM
    const list = byMinute.get(key);
    if (list) list.push(o);
    else byMinute.set(key, [o]);
  }
  return Array.from(byMinute.entries()).map(([key, items]) => ({
    key,
    submittedAt: items[0]!.submittedAt,
    items
  }));
}
